import React, { useEffect, useState, useCallback } from 'react';
import {
  ChevronRight, ChevronDown, Plus, Edit2, Trash2,
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, FolderTree
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import FormField from '../../components/common/FormField';
import Modal from '../../components/common/Modal';
import EmptyState from '../../components/common/EmptyState';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { categoryService } from '../../services/categoryService';

const MAX_DEPTH = 3;

// Achata uma árvore numa lista plana com depth/parentId/order para facilitar
// renderização visual indentada e ações up/down/indent/outdent.
function flattenTree(nodes, depth = 1, parentId = null, out = []) {
  if (!nodes) return out;
  for (const n of nodes) {
    out.push({ ...n, depth, parentId: n.parentId ?? parentId });
    if (n.children?.length) flattenTree(n.children, depth + 1, n.id, out);
  }
  return out;
}

const CategoriesManagement = () => {
  const { error, success } = useToast();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editing, setEditing] = useState(null); // { mode: 'create'|'edit', node }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await categoryService.tree();
      setTree(t || []);
      setExpanded((prev) => {
        const next = new Set(prev);
        flattenTree(t).forEach((n) => { if (n.children?.length) next.add(n.id); });
        return next;
      });
    } catch (e) {
      error(e.message);
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Toggle: se algum nó com filhos está colapsado → expande todos; senão colapsa.
  const expandableIds = () => {
    const ids = new Set();
    flattenTree(tree).forEach((n) => {
      if (n.children?.length) ids.add(n.id);
    });
    return ids;
  };

  const allExpanded = (() => {
    const ids = expandableIds();
    if (ids.size === 0) return false;
    for (const id of ids) {
      if (!expanded.has(id)) return false;
    }
    return true;
  })();

  const toggleAllExpanded = () => {
    setExpanded(allExpanded ? new Set() : expandableIds());
  };

  const renderNode = (node, level = 1) => {
    const hasChildren = (node.children || []).length > 0;
    const isOpen = expanded.has(node.id);
    return (
      <div key={node.id}>
        <div
          className="group relative"
          style={{ marginLeft: `${(level - 1) * 22}px` }}
        >
          {level > 1 && (
            <span className="absolute -left-4 top-0 bottom-0 w-px bg-app-border" />
          )}
          <div className="flex items-center gap-2 py-2 px-2 rounded hover:bg-app-surface-2 border border-transparent hover:border-app-border">
          <button
            onClick={() => hasChildren && toggle(node.id)}
            className={`text-app-text-secondary ${hasChildren ? '' : 'invisible'}`}
            aria-label={isOpen ? 'Colapsar' : 'Expandir'}
          >
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-app-surface text-app-text-secondary border border-app-border">
            Nível {level}
          </span>
          <span className="font-medium flex-1 truncate">
            {node.name}
            <span className="ml-2 text-xs text-app-text-secondary">/{node.slug}</span>
          </span>
          {!node.active && <span className="text-xs text-error">inativa</span>}
          <div className="hidden group-hover:flex items-center gap-1">
            <Button size="xs" variant="ghost" onClick={() => moveUp(node)} title="Mover para cima">
              <ArrowUp size={14} />
            </Button>
            <Button size="xs" variant="ghost" onClick={() => moveDown(node)} title="Mover para baixo">
              <ArrowDown size={14} />
            </Button>
            <Button size="xs" variant="ghost" onClick={() => outdent(node)} title="Promover (subir nível)">
              <ArrowLeft size={14} />
            </Button>
            <Button size="xs" variant="ghost" onClick={() => indent(node)} title="Rebaixar (descer nível)">
              <ArrowRight size={14} />
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setEditing({ mode: 'edit', node })} title="Editar">
              <Edit2 size={14} />
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setConfirmDelete(node)} title="Remover">
              <Trash2 size={14} className="text-error" />
            </Button>
            {level < MAX_DEPTH && (
              <Button
                size="xs" variant="subtle"
                onClick={() => setEditing({ mode: 'create', parent: node })}
                title="Adicionar filha"
              >
                <Plus size={14} />
              </Button>
            )}
          </div>
        </div>
        </div>
        {isOpen && hasChildren && (
          <div className="relative">
            {node.children.map((c) => renderNode(c, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Helpers para localizar irmãos / ancestrais.
  const flat = flattenTree(tree);

  const findSiblings = (node) =>
    flat.filter((n) => n.parentId === node.parentId);

  const moveUp = async (node) => {
    const siblings = findSiblings(node).sort((a, b) => a.orderIndex - b.orderIndex);
    const idx = siblings.findIndex((s) => s.id === node.id);
    if (idx <= 0) return;
    const items = siblings.map((s, i) => ({
      id: s.id,
      orderIndex: i === idx - 1 ? siblings[idx].orderIndex : i === idx ? siblings[idx - 1].orderIndex : s.orderIndex
    }));
    try { await categoryService.reorder(items); load(); } catch (e) { error(e.message); }
  };

  const moveDown = async (node) => {
    const siblings = findSiblings(node).sort((a, b) => a.orderIndex - b.orderIndex);
    const idx = siblings.findIndex((s) => s.id === node.id);
    if (idx === -1 || idx >= siblings.length - 1) return;
    const items = siblings.map((s, i) => ({
      id: s.id,
      orderIndex: i === idx + 1 ? siblings[idx].orderIndex : i === idx ? siblings[idx + 1].orderIndex : s.orderIndex
    }));
    try { await categoryService.reorder(items); load(); } catch (e) { error(e.message); }
  };

  // Promover = mover pra cima do parent (ficar irmão do parent).
  const outdent = async (node) => {
    if (!node.parentId) {
      success('Categoria já está no nível raiz');
      return;
    }
    const parent = flat.find((n) => n.id === node.parentId);
    try {
      await categoryService.reorder([{ id: node.id, parentId: parent?.parentId || null, orderIndex: (parent?.orderIndex || 0) + 1 }]);
      load();
    } catch (e) { error(e.message); }
  };

  // Rebaixar = vira filha do irmão imediatamente acima (mesmo parent).
  const indent = async (node) => {
    const siblings = findSiblings(node).sort((a, b) => a.orderIndex - b.orderIndex);
    const idx = siblings.findIndex((s) => s.id === node.id);
    if (idx <= 0) {
      error('Não é possível rebaixar — não há categoria acima no mesmo nível.');
      return;
    }
    const newParent = siblings[idx - 1];
    if (newParent.depth >= MAX_DEPTH) {
      error(`Limite de ${MAX_DEPTH} níveis atingido.`);
      return;
    }
    try {
      await categoryService.reorder([{ id: node.id, parentId: newParent.id, orderIndex: 9999 }]);
      load();
    } catch (e) { error(e.message); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await categoryService.remove(confirmDelete.id);
      success('Categoria removida');
      setConfirmDelete(null);
      load();
    } catch (e) { error(e.message); }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Categorias</h1>
          <p className="text-sm text-app-text-secondary">
            Organize seus produtos em até {MAX_DEPTH} níveis hierárquicos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={toggleAllExpanded}>
            {allExpanded ? 'Colapsar tudo' : 'Expandir tudo'}
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={() => setEditing({ mode: 'create', parent: null })}>
            Nova categoria
          </Button>
        </div>
      </div>

      <Card className="p-3">
        {loading ? (
          <p className="p-6 text-app-text-secondary">Carregando…</p>
        ) : tree.length === 0 ? (
          <EmptyState
            icon={FolderTree}
            title="Nenhuma categoria ainda"
            description="Crie a primeira categoria para organizar seus produtos."
          />
        ) : (
          <div>{tree.map((n) => renderNode(n, 1))}</div>
        )}
      </Card>

      {editing && (
        <CategoryFormModal
          mode={editing.mode}
          node={editing.node}
          parent={editing.parent}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={remove}
        title={`Remover ${confirmDelete?.name || 'categoria'}?`}
        message="Subcategorias e produtos associados podem ser afetados."
        confirmLabel="Remover"
        variant="danger"
      />
    </div>
  );
};

const CategoryFormModal = ({ mode, node, parent, onClose, onSaved }) => {
  const { error, success } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    name: node?.name || '',
    slug: node?.slug || '',
    active: node?.active ?? true
  }));

  const isEdit = mode === 'edit';

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) {
        await categoryService.update(node.id, {
          name: form.name,
          slug: form.slug || undefined,
          active: form.active
        });
        success('Categoria atualizada');
      } else {
        await categoryService.create({
          name: form.name,
          slug: form.slug || undefined,
          parentId: parent?.id || null,
          active: form.active
        });
        success('Categoria criada');
      }
      onSaved();
    } catch (e) { error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? `Editar ${node?.name}` : parent ? `Nova subcategoria de ${parent.name}` : 'Nova categoria'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} loading={busy}>{isEdit ? 'Salvar' : 'Criar'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Nome" required>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
        </FormField>
        <FormField label="Slug" helpText="Deixe vazio para gerar automaticamente.">
          <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
        </FormField>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
          />
          <span>Ativa</span>
        </label>
      </div>
    </Modal>
  );
};

export default CategoriesManagement;
